import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithMicrosoft } from '../lib/api';
import { AuthLayout } from '../components/AuthLayout';
import {
  getMicrosoftRedirectUri,
  MICROSOFT_PKCE_VERIFIER_KEY,
  MICROSOFT_PKCE_STATE_KEY,
} from '../lib/microsoftAuth';

const errorTextStyle: React.CSSProperties = {
  color: 'var(--color-danger)',
  fontSize: '0.875rem',
  marginBottom: 'var(--space-4)',
};

/** R-306: handles the redirect back from Microsoft's /authorize endpoint,
 * exchanges the code for a session, and hands off to SignInPage to finish
 * (persist tokens, prompt for a name if missing, or finalize). */
export function MicrosoftCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode's double-invoke in dev, which would
    // otherwise read+clear the single-use sessionStorage entries twice.
    if (ran.current) return;
    ran.current = true;

    async function run() {
      const params = new URLSearchParams(window.location.search);
      const oauthError = params.get('error');
      const oauthErrorDescription = params.get('error_description');
      const code = params.get('code');
      const state = params.get('state');

      // Read and immediately clear the single-use PKCE state, regardless of
      // outcome below.
      const storedState = sessionStorage.getItem(MICROSOFT_PKCE_STATE_KEY);
      const storedVerifier = sessionStorage.getItem(MICROSOFT_PKCE_VERIFIER_KEY);
      sessionStorage.removeItem(MICROSOFT_PKCE_STATE_KEY);
      sessionStorage.removeItem(MICROSOFT_PKCE_VERIFIER_KEY);

      if (oauthError) {
        setError(oauthErrorDescription || 'Microsoft sign-in was cancelled.');
        return;
      }

      if (!code || !state) {
        setError('Microsoft did not return an authorization code. Please try again.');
        return;
      }

      if (!storedState || state !== storedState) {
        setError('Sign-in state mismatch. Please try again.');
        return;
      }

      if (!storedVerifier) {
        setError('Your sign-in session expired. Please try again.');
        return;
      }

      // Same derivation SignInPage used to build the /authorize URL — must
      // match exactly, since Microsoft's token endpoint requires the
      // redirect_uri sent here to equal the one sent to /authorize.
      const redirectUri = getMicrosoftRedirectUri();

      const result = await signInWithMicrosoft(code, storedVerifier, redirectUri);
      if (result.kind === 'error') {
        setError(result.message);
        return;
      }

      navigate('/sign-in', {
        replace: true,
        state: {
          pendingSession: {
            accessToken: result.accessToken,
            refreshToken: result.refreshToken,
            accessExpiresAt: result.accessExpiresAt,
          },
        },
      });
    }

    run();
  }, [navigate]);

  return (
    <AuthLayout>
      {error ? (
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: 'var(--space-3)' }}>Sign-in failed</h2>
          <p role="alert" style={errorTextStyle}>
            {error}
          </p>
          <Link to="/sign-in" className="btn btn--ghost">
            Back to sign in
          </Link>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 'var(--space-4) 0' }}>
          <p style={{ color: 'var(--color-text-muted)' }}>Signing you in…</p>
        </div>
      )}
    </AuthLayout>
  );
}
